'use strict';

const materialIcon = require('../materialIcon');
const { showToastMessage } = require('../../js/components/toast');

/**
 * Updates the notify-me trigger label after (un)subscribing.
 * The preorder link uses its link text without icon, the regular button keeps text and icon.
 * @param {HTMLElement} notificationButton - notify-me trigger element
 * @param {boolean} subscribed - new subscription state
 */
function updateNotificationButton(notificationButton, subscribed) {
    const { dataset } = notificationButton;
    const iconName = subscribed ? 'notifications_off' : 'notifications_none';
    const label = subscribed
        ? dataset.notificationSubscribedLinkText || dataset.notificationSubscribedText
        : dataset.notificationUnsubscribedLinkText || dataset.notificationUnsubscribedText;
    const iconHtml = dataset.notificationHideIcon ? '' : `<span class="me-1 lh-1">${materialIcon[iconName]}</span> `;

    notificationButton.innerHTML = iconHtml + label;
    dataset.notificationSubscribed = String(subscribed);
}

/**
 * Displays the response message.
 * 2F - 2Factor authentification case (user needs to confirm via mail next)
 * @param {Object} data - data returned from the server's ajax call
 * @param {Object} button - button that was clicked for email sign-up
 */
function displayMessage(data) {
    $.spinner().stop();

    if (data.email2FSent) {
        const alertType = data.success ? 'alert-warning' : 'alert-danger';

        showToastMessage({
            message: data.msg1 + ' ' + data.msg2,
            alertType: alertType,
            alertQa: alertType.replace('alert-', 'pdp-notify-me-') + '-alert',
            disableAutoClosing: true,
            disableCloseButton: false
        });
    } else {
        const alertType = data.success ? 'alert-success' : 'alert-danger';

        showToastMessage({
            containerSelector: data.containerSelector || '.feedback-element',
            message: data.msg,
            alertType: alertType,
            alertQa: alertType.replace('alert-', 'pdp-notify-me-') + '-alert',
            disableAutoClosing: true,
            disableCloseButton: data.disableCloseButton || false
        });
    }

    if (data.success) {
        const $modal = $('#notify-me-modal');
        $modal.modal('hide');
        const productID = $modal.find('.notify-me-product-id').val();
        const notificationButton = document.querySelector('.btn.notify-me[data-pid=' + productID + ']');
        if (notificationButton) {
            if (data.subscribedToNotification) {
                updateNotificationButton(notificationButton, true);
            } else if (data.unsubscribedNotification) {
                updateNotificationButton(notificationButton, false);
            }
        }
    }
}

/**
 * Subscribes to a URL and displays the response message.
 *
 * @param {Object} notifyMeForm - The URL to subscribe to.
 * @return {void} This function does not return anything.
 */
function submitNotifyMe(notifyMeForm) {
    const url = notifyMeForm.getAttribute('action');
    let params = new FormData(notifyMeForm);
    fetch(url, {
        method: 'post',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(params)
    }).then(res => {
        return res.json().then(data => {
            if (res.ok) {
                return data;
            }
            data.httpStatus = res.status;
            data.httpStatusText = res.statusText;
            return Promise.reject(data);
        });
    }).then(data => {
        displayMessage(data);
    }).catch(err => {
        displayMessage({
            msg: err.statusText || err.httpStatusText || notifyMeForm.dataset.errorMessage,
            containerSelector: '.notify-me-modal-feedback-element',
            disableCloseButton: true
        });
    });
}

module.exports = {
    handleNotifyMe: function () {
        const notifyMeForm = document.querySelector('.notify-me-form-js');
        notifyMeForm?.addEventListener('submit', function (e) {
            e.preventDefault();

            let renderCaptchaElement = notifyMeForm.querySelector('.g-recaptcha');
            if (renderCaptchaElement && !this.classList.contains('subscribed-js')) {
                // eslint-disable-next-line no-undef
                grecaptcha.ready(function () {
                    const siteKey = renderCaptchaElement.dataset.sitekey;
                    // eslint-disable-next-line no-undef
                    grecaptcha.execute(siteKey, { action: 'notifyme' }).then(function (token) {
                        notifyMeForm.querySelector('input[name="responseToken"]').value = token;
                        $(notifyMeForm).spinner().start();
                        submitNotifyMe(notifyMeForm);
                    });
                });
            } else {
                $.spinner().start();
                submitNotifyMe(notifyMeForm);
            }
        });

        const notifyMeModal = document.getElementById('notify-me-modal');
        if (notifyMeModal) {
            notifyMeModal.addEventListener('show.bs.modal', function (event) {
                const button = event.relatedTarget;
                const modalForm = this.querySelector('.notify-me-form-js');
                const emailAddressField = modalForm.querySelector('.notify-me-email');
                const productIDField = modalForm.querySelector('.notify-me-product-id');
                const modalTitle = this.querySelector('.modal-title');
                const notifymeLabel = this.querySelector('.js-notifyme-label');
                const modalSubmitButton = this.querySelector('.notify-me-submit-js');

                modalForm.action = button.dataset.action;
                emailAddressField.value = button.dataset.emailAddress ? button.dataset.emailAddress : '';
                productIDField.value = button.dataset.pid;
                if (button.dataset.disableEmailInputField) {
                    emailAddressField.setAttribute('readonly', true);
                }

                if (button.dataset.notificationSubscribed === 'true') {
                    modalForm.action = button.dataset.unsubscribeAction;
                    modalForm.classList.add('subscribed-js');
                    modalTitle.innerText = button.dataset.notificationSubscribedTitle;
                    modalSubmitButton.innerHTML = `<span class="me-1 lh-1">${materialIcon.notifications_off}</span> ${button.dataset.notificationSubscribedText}`;
                    notifymeLabel.classList.add('d-none');
                } else {
                    modalForm.action = button.dataset.action;
                    modalForm.classList.remove('subscribed-js');
                    modalTitle.innerText = button.dataset.notificationUnsubscribedTitle;
                    modalSubmitButton.innerHTML = `<span class="me-1 lh-1">${materialIcon.notifications_none}</span> ${button.dataset.notificationUnsubscribedText}`;
                    notifymeLabel.classList.remove('d-none');
                }
            });
        }
    },
    displayMessageEmailNotificationConfirmation: function () {
        const notificationButton = $('.btn.notify-me');
        const notificationConfirmation = notificationButton?.data('notification-confirmation');

        if (notificationButton.length === 0 || !notificationConfirmation) {
            return;
        }
        const notificationConfirmationSuccess = notificationButton.data('notification-confirmation-success');
        const notificationConfirmationMsg = notificationButton.data('notification-confirmation-msg');
        const alertType = notificationConfirmationSuccess ? 'alert-success' : 'alert-info';

        showToastMessage({
            message: notificationConfirmationMsg,
            alertType: alertType,
            disableAutoClosing: true,
            disableCloseButton: false
        });
    }
};
